import { push } from 'connected-react-router';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { exportInvoices, loadInvoices, uploadInvoices } from '../../ducks';

import { RouteComponentProps, withRouter } from 'react-router';
import { LIMIT_PER_PAGE } from 'src/core/constants';
import { createErrorNotification, createSuccessNotification } from 'src/core/services/createNotification';
import { currentVendorIdSelector } from 'src/vendors/services/currentVendorSelector';
import {
  PageActions,
  PageContent,
  PageDetails,
  PageHeader,
  PageSubtitle,
  PageTitle,
  PageTitleContainer,
} from '../../../common/components/styled';
import { DuckFunction } from '../../../contracts/ducks';
import { Pagination, Table } from '../../../core/components';
import { Button, Panel, PanelSection, PanelSectionGroup, Text } from '../../../core/components/styled';
import { SortOrder } from '../../../core/components/Table';
import translate from '../../../core/services/translate';
import { getTextWithLinks } from '../../../landing/components/services/Utils';
import { AppState } from '../../../store';
import { createUrl, getQueryParams } from '../../../utils/services/queryParams';
import { InvoiceSearchParams } from '../../interfaces/InvoiceSearchParams';
import { InvoiceForm, InvoiceFormTableRow } from '../forms';
import UploadWorkOrdersModal from '../modal/UploadWorkOrdersModal';
import { AdditionalInfo, NoResultsContainer } from '../styled/Invoice';

interface Props extends RouteComponentProps {
  invoices?: any;
  total?: number;
  isLoading?: boolean;
  loadInvoices: DuckFunction<typeof loadInvoices>;
  exportInvoices: DuckFunction<typeof exportInvoices>;
  uploadInvoices: DuckFunction<typeof uploadInvoices>;
  push: any;
  currentVendorId: number;
  searchFormInitialValues: any;
}

const invoiceTableCells = [
  { name: 'VendorInvoiceNbr', label: translate('invoice.invoiceNumber'), width: '20%', sortable: true },
  { name: 'AccountNbr', label: translate('common.account'), width: '20%', sortable: true },
  { name: 'Status', label: translate('invoice.status'), width: '10%', sortable: true },
  { name: 'InvoiceDate', label: translate('invoice.invoiceDate'), width: '10%', sortable: true },
  { name: 'ReceivedDate', label: translate('invoice.receivedDate'), width: '10%', sortable: true },
  { name: 'InvoiceAmount', label: translate('invoice.invoiceAmount'), width: '10%', sortable: true },
  { name: 'PaymentAmount', label: translate('invoice.paymentAmount'), width: '10%', sortable: true },
  { name: 'CheckDate,CheckNumber,ReferenceNumber,PaymentMethod', label: translate('invoice.paymentInfo'), width: '10%', sortable: true },
];

interface State {
  isUploadInvoicesModalOpen: boolean;
}

class InvoicePage extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isUploadInvoicesModalOpen: false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    const { location, loadInvoices, searchFormInitialValues } = this.props;

    if (location.search !== prevProps.location.search) {
      const { from, to, invoiceNumber, accountNumber, sortedBy = 'VendorInvoiceNbr', sortOrder = 'asc', page = 1 } = getQueryParams(location.search);

      const invoiceSearchParams: InvoiceSearchParams = {
        vendorGroup: searchFormInitialValues.vendorGroup,
        invoiceNumber: invoiceNumber || searchFormInitialValues.invoiceNumber,
        accountNumber: accountNumber || searchFormInitialValues.accountNumber,
        invoiceStartDate: from || searchFormInitialValues.dateRange.from || '',
        invoiceEndDate: to || searchFormInitialValues.dateRange.to || '',
        orderBy: sortedBy,
        orderByDirection: sortOrder === 'desc' ? 'DESC' : 'ASC',
      };

      loadInvoices(invoiceSearchParams, parseInt(page) || 1, LIMIT_PER_PAGE);
    }
  }

  onInvoiceUpload = async (object: any) => {
    const { uploadInvoices, currentVendorId } = this.props;
    try {
      await uploadInvoices(object, currentVendorId);
      createSuccessNotification(translate('opportunity.uploadSuccess'));
    } catch (err) {
      createErrorNotification(translate('opportunity.uploadError'));
    }
  };

  onSubmit = async (formData: any) => {
    const { dateRange, accountNumber, invoiceNumber } = formData;

    this.loadInvoices({
      from: dateRange?.from,
      to: dateRange?.to,
      invoiceNumber: invoiceNumber,
      accountNumber: accountNumber,
      page: 1,
    });
  };

  loadInvoices = (updatedProps: any) => {
    const { location: { pathname, search }, push } = this.props;
    const { from, to, invoiceNumber, accountNumber, sortedBy, sortOrder, page = 1 } = getQueryParams(search);

    push(
      createUrl(pathname, search, {
        from,
        to,
        invoiceNumber,
        accountNumber,
        sortedBy,
        sortOrder,
        page,
        ...updatedProps,
      }),
    );
  };

  onSortOrderChange = (sortedBy: string, sortOrder: SortOrder) => {
    this.loadInvoices({ sortedBy, sortOrder, page: 1 });
  };

  onExport = () => {
    const { exportInvoices, searchFormInitialValues, location: { search } } = this.props;
    const { from, to, invoiceNumber, accountNumber } = getQueryParams(search);

    exportInvoices({
      vendorGroup: searchFormInitialValues.vendorGroup,
      invoiceNumber: invoiceNumber || searchFormInitialValues.invoiceNumber,
      accountNumber: accountNumber || searchFormInitialValues.accountNumber,
      invoiceStartDate: from || searchFormInitialValues.dateRange.from,
      invoiceEndDate: to || searchFormInitialValues.dateRange.to,
    });
  };

  openInvoiceModal = () => {
    this.setState({ isUploadInvoicesModalOpen: true });
  };

  closeInvoiceModal = () => {
    this.setState({ isUploadInvoicesModalOpen: false });
  };

  render() {
    const { searchFormInitialValues, invoices, total, isLoading, location } = this.props;
    const { isUploadInvoicesModalOpen } = this.state;
    const { sortedBy = 'VendorInvoiceNbr', sortOrder = 'asc' } = getQueryParams(location.search);

    return (
      <>
        <PageContent>
          <PageHeader withSubTitle>
            <PageDetails>
              <PageTitleContainer>
                <PageTitle>{translate('invoice.invoices')}</PageTitle>
              </PageTitleContainer>
            </PageDetails>
            <PageActions align="right">
              <Button onClick={this.openInvoiceModal} margin="small" color="primary">
                Upload
              </Button>
              <Button onClick={this.onExport} margin="small" color="primary">
                {translate('common.export')}
              </Button>
            </PageActions>
          </PageHeader>
          <Panel>
            <PanelSectionGroup>
              <PanelSection>
                <InvoiceForm initialValues={searchFormInitialValues} onSubmit={this.onSubmit} />
              </PanelSection>
              <PanelSection vertical isLoading={isLoading}>
                <Table
                  cells={invoiceTableCells}
                  rows={invoices || []}
                  rowComponent={InvoiceFormTableRow}
                  sortedBy={sortedBy}
                  sortOrder={sortOrder}
                  sort={this.onSortOrderChange}
                />
                {!invoices.length && (
                  <NoResultsContainer>
                    <PageSubtitle>{translate('invoice.noInvoicesFound')}</PageSubtitle>
                    <div>{translate('invoice.noInvoicesFoundAdditional')}</div>
                  </NoResultsContainer>
                )}
              </PanelSection>
              {!!total && <Pagination totalResults={total} />}
            </PanelSectionGroup>
          </Panel>
          <PanelSectionGroup>
            <AdditionalInfo>
              <Text block weight="normal" size="medium" margin="xSmall xSmall xxSmall">
                {getTextWithLinks(translate('invoice.messages.inquiryMessage'))}
              </Text>
            </AdditionalInfo>
            <AdditionalInfo>
              <Text block weight="normal" size="medium" margin="xSmall xSmall xxSmall">
                {getTextWithLinks(translate('invoice.messages.invoiceMessage'))}
              </Text>
            </AdditionalInfo>
          </PanelSectionGroup>
          {isUploadInvoicesModalOpen && (
            <UploadWorkOrdersModal
              uploadFile={this.onInvoiceUpload}
              vendorId={56}
              closeModal={this.closeInvoiceModal}
            />
          )}
        </PageContent>
      </>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  invoices: state.opportunity.invoice.invoices,
  total: state.opportunity.invoice.total || 0,
  isLoading: state.opportunity.invoice.isLoading || state.opportunity.invoice.isExporting,
  currentVendorId: (currentVendorIdSelector as any)(state.account.login, state.vendors.defaultVendor),
});

const mapDispatchToProps = {
  loadInvoices,
  exportInvoices,
  push,
  uploadInvoices,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(InvoicePage));
